import { useCfmContext } from "../Context/CfmContext";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { storage } from "../../../../backend/firebaseconfig";
import { ref, getDownloadURL } from "firebase/storage";
import { getCashflowModellerData } from "../FirebaseHelpers/FirebaseHelpers";
import { useNavigate } from "react-router-dom";
import { deleteCashflowEntry } from "../FirebaseHelpers/FirebaseHelpers";


/**
 * Custom hook providing actions for handling client-related operations in the Cashflow Modeller.
 *
 * @returns {Object} Actions for client management.
 * @returns {Function} handleClient - Loads and sets up client data in context, navigates to the modeller view, or returns state for preview.
 * @param {Object} handleClient.entry - The client entry object containing client details.
 * @param {string} [handleClient.action="view"] - The action to perform: "view" navigates to the modeller, "preview" returns the current state.
 * @returns {Promise<void|Object>} If action is "view", navigates to modeller; if "preview", returns the current state.
 *
 * @returns {Function} handleDelete - Deletes a client entry if the current user is the creator.
 * @param {Object} handleDelete.entry - The client entry object to delete.
 * @param {Function} [handleDelete.onDeleted] - Optional callback invoked after successful deletion with the deleted entry's ID.
 * @returns {Promise<void>}
 */
export const useClientActions = () => {
  const { state, dispatch } = useCfmContext();
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  // const { userPermissions } = useUserPermissions();
  const navigate = useNavigate();

  /**
   * Handles loading and setting up client data for the Cashflow Modeller.
   *
   * Depending on the action, this function fetches client data, resolves the logo if needed,
   * updates local storage and Redux state, and navigates to the modeller view or returns the preview state.
   *
   * @async
   * @function
   * @param {Object} entry - The client entry object containing client details.
   * @param {string} [action="view"] - The action to perform: "view" navigates to the modeller, "preview" returns the state.
   * @returns {Promise<Object|undefined>} Returns the current state if action is "preview", otherwise undefined.
   *
   * @throws Will not throw, but will silently fail if required data is missing or logo cannot be resolved.
   *
   * @example
   * await handleClient(clientEntry, "view");
   * const previewState = await handleClient(clientEntry, "preview");
   */
  const handleClient = async (entry, action = "view") => {
    // console.log("Handling client:", entry);
    if (!entry?.id) return;

    const isOwnClient = entry.createdBy === userEmail;
    const roleToUse = entry.role || "Staff";
    const emailToUse = entry.createdBy;

    let resolvedLogo = "";
    if (!isOwnClient) {
      try {
        const logoRef = ref(storage, `Logos/${entry.tradingName}.jpg`);
        resolvedLogo = await getDownloadURL(logoRef);
      } catch {
        resolvedLogo = "";
      }
    }

    const data = await getCashflowModellerData(roleToUse, emailToUse, entry.id);
    // console.log("Fetched client data:", data);
    if (!data) return;

    localStorage.removeItem("cfmContextData");
    dispatch({ type: "RESET_CONTEXT" });
    dispatch({ type: "SET_DOC_ID", payload: entry.id });

    const payload = {
      ...data,
      createdBy: entry.createdBy,
      tradingName: entry.tradingName,
      primaryColor: entry.primaryColor,
      logo: resolvedLogo,
      regulatoryText: entry.regulatoryText || "",
      roleToUse: roleToUse
    };

    localStorage.setItem("cfmContextData", JSON.stringify(data));
    dispatch({ type: "LOAD_ALL", payload });

    // console.log("Loaded client data:", payload);

    // console.log("Previewing client state:", state);

    if (action === "view") {
      setTimeout(() => {
        navigate("/cashflow-modeller");
      }, 50);
    }

    if (action === "preview") {
      return state; // caller will set previewClientState
    }
  };

  /**
   * Handles the deletion of a cashflow entry if the current user is the creator.
   *
   * @async
   * @function
   * @param {Object} entry - The cashflow entry object to be deleted.
   * @param {string|number} entry.id - The unique identifier of the entry.
   * @param {string} entry.createdBy - The email of the user who created the entry.
   * @param {Function} [onDeleted] - Optional callback to be called after successful deletion, receives the deleted entry's id.
   * @returns {Promise<void>} Resolves when the deletion is complete or if deletion is not permitted.
   *
   * @throws {void} Does not throw, but logs a warning if attempting to delete another user's client.
   */
  const handleDelete = async (entry, onDeleted) => {
    if (!entry?.id) return;

    // Only allow deleting own clients
    if (entry.createdBy !== userEmail) {
      // console.warn("Cannot delete other users' clients");
      return;
    }

    const roleToUse = entry.role || userRole; // fall back to current role
    const emailToUse = entry.createdBy; // parent/scenarios live under this user

    try {
      // 1) Fetch parent to see if it has scenarios
      const parentData = await getCashflowModellerData(
        roleToUse,
        emailToUse,
        entry.id
      );
      const scenarioIds = Array.isArray(parentData?.scenarios)
        ? parentData.scenarios.map((s) => s?.id).filter(Boolean)
        : [];

      // 2) Delete all scenario docs (ignore individual failures so others still delete)
      await Promise.all(
        scenarioIds.map((sid) =>
          deleteCashflowEntry(roleToUse, emailToUse, sid).catch((e) => {
            // console.warn(`Failed deleting scenario ${sid}:`, e);
            return null;
          })
        )
      );

      // 3) Delete the parent doc
      await deleteCashflowEntry(roleToUse, emailToUse, entry.id);

      if (onDeleted) onDeleted(entry.id);
    } catch (err) {
      // console.error("Failed to delete client and scenarios:", err);
    }
  };

  // inside your hook/component where userEmail/userRole/deleteCashflowEntry are in scope
  const handleDeleteScenario = async (entry, onDeleted) => {
    // expect the active scenario's doc id on entry.docId
    if (!entry?.docId) return;

    // Only allow deleting own clients/scenarios
    const isOwnClient = (entry.createdBy || userEmail) === userEmail;
    if (!isOwnClient) {
      // console.warn("Cannot delete other users' scenarios");
      return;
    }

    await deleteCashflowEntry(userRole, userEmail, entry.docId);
    if (onDeleted) onDeleted(entry.docId);
  };

  return {
    handleClient,
    handleDelete,
    handleDeleteScenario,
  };
};
